<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Locales } from '@/services/types/api.types';

const { t } = useI18n();

const props = defineProps<{
  languageEnabled: Locales[];
  selectedLanguage?: Locales | null;
}>();

const emit = defineEmits<{
  (e: 'analyze', language?: Locales): void;
  (e: 'update:selectedLanguage', language: Locales): void;
}>();

const languageOptions = computed(() =>
  props.languageEnabled.map((language) => ({
    label: t(`languages.${language}`),
    value: language,
  })),
);

const showLanguageSelector = computed(() => props.languageEnabled.length > 1);

const selectedLanguage = computed({
  get: () => props.selectedLanguage ?? props.languageEnabled[0],
  set: (language: Locales) => emit('update:selectedLanguage', language),
});
</script>

<template>
  <div class="empty-state q-pa-xl text-center">
    <q-icon name="analytics" size="64px" color="grey-5" class="q-mb-md" />
    <div class="text-h6 text-grey-7 q-mb-sm">{{ t('consultation.ai.emptyState.title') }}</div>
    <div class="text-body2 text-grey-6 q-mb-lg">{{ t('consultation.ai.emptyState.description') }}</div>
    <q-select
      v-if="showLanguageSelector"
      v-model="selectedLanguage"
      :options="languageOptions"
      emit-value
      map-options
      outlined
      dense
      style="min-width: 280px"
      class="q-mb-md"
      :label="t('consultation.ai.analysisLanguage.label')"
    />
    <q-btn
      color="primary"
      :label="t('consultation.ai.emptyState.action')"
      @click="emit('analyze', selectedLanguage)"
      unelevated
    />
  </div>
</template>

<style scoped>
.empty-state {
  border: 2px dashed #e0e0e0;
  border-radius: 8px;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
</style>
